/* eslint-disable no-console */
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { CustomInputWithController } from "../../Core/Components/CustomInput";
import CustomLayout from "../../Core/Layout/CustomLayout";
import Button from "../../Core/Components/CustomButton";
import { PopoverDropdown } from "../../Core/Components/CustomDropdown";
import useHubStationOnAdd from "../../Utils/useHubStationOnAdd";
import { CONTAINER_TYPE } from "./data";
import { updateContainer } from "../../Redux/Slices/container.slice";
import { addContainer, editContainer } from "../../Redux/APIs/containersAPI";

const LabelAndInput = ({
  label,
  control,
  formState,
  name,
  placeholder,
  rules,
  focus,
  setFocus,
}) => {
  return (
    <div className="">
      <p className="font-aileron text-xs font-normal text-dark-gray capitalize mb-1.5">
        {label}
      </p>
      <CustomInputWithController
        intent="createUser"
        control={control}
        formState={formState}
        focus={focus}
        setFocus={(e) => setFocus(e)}
        name={name}
        placeholder={placeholder}
        rules={rules}
      />
    </div>
  );
};

const AddContainer = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { id } = useParams();

  // get all the hubs
  const hubStations = useHubStationOnAdd();

  const { currentContainer } = useSelector((state) => state.containers);

  const {
    handleSubmit,
    control,
    formState,
    setValue,
    clearErrors,
    watch,
    setError,
  } = useForm({
    defaultValues: {
      type: id ? currentContainer?.type : CONTAINER_TYPE.CANISTER,
      containerId: id ? currentContainer?.id : 0,
      containerName: id ? currentContainer?.containerName : "",
      hubId: id ? currentContainer?.currentHubId : 0,
      hubName: id ? currentContainer?.currentHubName : "",
    },
  });

  const [focus, setFocus] = useState({
    name: false,
    tagNo: false,
    tbId: false,
  });

  const rules = {
    required: "Required.",
  };

  const hubNameError = formState?.errors?.hubName?.message;

  const onCancel = () => navigate(-1);

  const onAdd = async (e) => {
    const payload = {
      containerName: e.containerName.trim(),
      type: Number(e.type),
      hubId: Number(e.hubId),
    };

    if (id) {
      payload.containerId = Number(e.containerId);

      const editResponse = await dispatch(editContainer(payload));

      if (
        editResponse?.payload?.response &&
        editResponse?.payload?.response?.status !== 200
      ) {
        setError(
          editResponse?.payload?.response?.data?.type || "containerName",
          {
            type: "server",
            shouldFocus: true,
            message: editResponse?.payload?.response?.data?.message,
          },
        );
      } else {
        dispatch(
          updateContainer({
            key: "showToastMessage",
            value: {
              visible: true,
              data: {
                title: "1 Container Edited",
                description: `Container ${e.containerName} was edited successfully`,
              },
            },
          }),
        );

        navigate(-1);
      }
    } else {
      const addResponse = await dispatch(addContainer(payload));

      if (
        addResponse?.payload?.response &&
        addResponse?.payload?.response?.status !== 200
      ) {
        setError(
          addResponse?.payload?.response?.data?.type || "containerName",
          {
            type: "server",
            shouldFocus: true,
            message: addResponse?.payload?.response?.data?.message,
          },
        );
      } else {
        dispatch(
          updateContainer({
            key: "showToastMessage",
            value: {
              visible: true,
              data: {
                title: "1 Container Added",
                description: `Container ${e.containerName} was added successfully`,
              },
            },
          }),
        );

        navigate(-1);
      }
    }
  };

  return (
    <CustomLayout pageName="Containers" hideFooter>
      <div className="px-6 pt-6 bg-background-white">
        <div className="flex items-center justify-between pb-6">
          <p className="font-poppins text-base font-medium capitalize">
            {id ? "edit container" : "add a new container"}
          </p>

          <div className="flex items-center gap-x-3">
            <Button
              size="filter"
              width="generateUser"
              colors="grey"
              onClick={onCancel}
            >
              Cancel
            </Button>

            <Button
              size="filter"
              width="generateUser"
              type="submit"
              onClick={handleSubmit(onAdd)}
            >
              {id ? "Save" : "Add"} Container
            </Button>
          </div>
        </div>

        <div className="w-full bg-light-grey h-[1px]" />
      </div>

      <div className="h-[calc(100vh-88px-72px)] bg-background-white p-6 flex flex-col gap-y-6 overflow-y-scroll">
        <div className="w-[367px]">
          <p className="font-aileron text-xs font-normal text-dark-gray capitalize mb-1.5">
            Container Type
          </p>

          <div className="flex items-center gap-x-1.5">
            <div
              role="button"
              className={`w-full h-8 flex items-center justify-center rounded-md transform transition-colors duration-300 ${
                +watch("type") === CONTAINER_TYPE.CANISTER
                  ? "bg-green text-white"
                  : "bg-green-10 text-primary"
              }`}
              onClick={() => setValue("type", CONTAINER_TYPE.CANISTER)}
            >
              <p className="font-poppins text-xs font-medium leading-[100%]">
                Canister
              </p>
            </div>

            <div
              role="button"
              className={`w-full h-8 flex items-center justify-center rounded-md transform transition-colors duration-300 ${
                +watch("type") === CONTAINER_TYPE.CRATE
                  ? "bg-green text-white"
                  : "bg-green-10 text-primary"
              }`}
              onClick={() => setValue("type", CONTAINER_TYPE.CRATE)}
            >
              <p className="font-poppins text-xs font-medium leading-[100%]">
                Crate
              </p>
            </div>
          </div>
        </div>

        <LabelAndInput
          label="Container ID"
          control={control}
          formState={formState}
          name="containerName"
          placeholder="Enter ID"
          rules={rules}
          focus={focus.name}
          setFocus={(state) => setFocus({ ...focus, name: state })}
        />

        <div className="">
          <p className="font-aileron text-xs font-normal text-dark-gray capitalize mb-1.5">
            Hub Station
          </p>

          <Controller
            name="hubName"
            control={control}
            rules={rules}
            render={({ field }) => (
              <PopoverDropdown
                {...field}
                enableSearch
                searchPlaceholder="Search Hub Station"
                placeholder="Assign Hub Station"
                className="max-h-[300px] overflow-y-scroll"
                formState={formState}
                options={hubStations}
                onSelect={(data) => {
                  setValue("hubName", data.value);
                  setValue("hubId", data.id);
                  clearErrors("hubName");
                }}
              />
            )}
          />

          <p
            className={`font-poppins text-xs font-light capitalize leading-4.5 ${
              hubNameError ? "text-alert-red visible mt-2" : "invisible"
            }`}
          >
            {hubNameError}
          </p>
        </div>
      </div>
    </CustomLayout>
  );
};

export default AddContainer;
